import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import { useCallback, useContext, useLayoutEffect, useMemo } from 'react'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import ModalContext from 'contexts/ModalContext'
import { trackOfferPageSuggestedPackageViewPackageClick } from 'components/OfferPage/analytics'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { addFlightPrice } from 'lib/offer/pricing'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { isRewardFareClass, toBaseFareClass } from 'constants/flight'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useIntl } from 'react-intl'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { rewriteOneWayCharterInclusions, getFeederOriginAirportCode, isConfiguredFeederOriginAirportCode, rewriteCharterFeederInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { openOfferPageRoomGalleryLightbox } from './OfferPageRoomGallery'
import { trackEvent } from 'analytics/analytics'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import uuidV4 from 'lib/string/uuidV4Utils'
import scrollToVisibleOfferPageRoom, { offerPageRoomAnchorExists } from './scrollToVisibleOfferPageRoom'
import SuggestedOptionPreviewRebrand from './SuggestedOptionPreviewRebrand'
import type { SuggestedOptionPreviewViewProps } from './suggestedOptionPreviewTypes'

interface Props {
  suggestedOption: App.OfferPackageOptionView
  message: string
  /** Fallback behaviour when clicking "View package", for Safari and other crap browsers that don't support `checkVisibility` */
  viewPackageFallback: () => void
}

function SuggestedOptionPreview({ suggestedOption, message, viewPackageFallback }: Props) {
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const offerPageDispatch = useOfferPageDispatch()
  const showLightbox = useContext(ModalContext)
  const roomGalleryListId = useMemo(() => uuidV4(), [])
  const { offer, checkInDate, duration, charterBundleRoute, priceMode, rooms, fareClass, airport: flightOrigin, expandedPackageInclusions } = offerPageState
  const isSameRoomMode = useAppSelector(appState => !isMultipleRoomBookingActive(offerPageState, appState))

  const { totalFlightPrice: flightPrice, rewardPreviewRate, rewardAvailable, rewardSeatPointsTotal, rewardSeatCashCoPayTotal, hasFeederFlightPriced, oneWayBundle } = useOfferPageFlightPrice()

  const pricing = suggestedOption.pricing
  const memberPricing = suggestedOption.memberPricing
  const pricingWithFlights = flightPrice ? addFlightPrice(pricing, flightPrice) : undefined
  const memberPricingWithFlights = (memberPricing && flightPrice) ? addFlightPrice(memberPricing, flightPrice) : undefined

  // Charter bundle fallback pricing (matches FlashOfferSummary). Needed when no specific check-in
  // is selected, or when the check-in doesn't match a charter date — otherwise the flight
  // portion above falls back to `metadata.cheapest.totalPrice`, which is the search-response
  // aggregate and ignores Friends & Family pricing. The fallback walks per-date calendar
  // entries (each FNF-aware via `useFlightItemData`), so the summary, offer tile and this
  // suggested-option preview all surface the same FNF-correct floor price.
  const suggestedOptionPkg = suggestedOption.package
  // Both hooks take the base cabin (cash) — reward variants share dates/journey data with
  // their base cabin so the date / fallback bundle is identical.
  const baseFareClassForFallback = toBaseFareClass(fareClass)
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, flightOrigin)
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate,
    nights: suggestedOptionPkg.duration,
    charterBundleRoute,
    rooms,
    fareClass: baseFareClassForFallback,
    feederOriginAirportCode,
  })
  const isCheckInMatchingCharterDate = !!checkInDate && !!charterDepartDate
  const shouldUseFallbackPricing = !!charterBundleRoute && (!checkInDate || !isCheckInMatchingCharterDate)
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass: baseFareClassForFallback,
    nights: suggestedOptionPkg.duration,
    disabled: !shouldUseFallbackPricing,
    feederOriginAirportCode,
  })

  const forceBundle = !!(charterBundleRoute || offer?.bundledWithFlightsOnly)
  const pricingToShow = !forceBundle
    ? pricing
    : shouldUseFallbackPricing && fallbackPricing
      ? fallbackPricing
      : pricingWithFlights
  const memberPricingToShow = !forceBundle
    ? memberPricing
    : shouldUseFallbackPricing && fallbackPricing
      ? fallbackMemberPricing
      : memberPricingWithFlights

  const pkg = suggestedOption.package
  const roomType = pkg.roomType
  const firstImage = roomType?.images?.[0]

  const { inclusions, bonusInclusions } = useOfferInclusions(pkg)

  const isCharterBundle = offer?.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  const charterRouteInfo = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
  const isFeederIncluded = isCharterBundle &&
    isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) &&
    hasFeederFlightPriced

  const effectiveInclusions = useMemo(() => {
    const { originAirport, destinationAirport } = charterRouteInfo
    if (oneWayBundle) {
      return rewriteOneWayCharterInclusions(inclusions, {
        feederCity: isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) ? flightOrigin?.name : undefined,
        originCity: originAirport.cityDesc,
        destinationCity: destinationAirport.cityDesc,
        intl,
      })
    }
    if (!isFeederIncluded || !flightOrigin) {
      return inclusions
    }

    return rewriteCharterFeederInclusions(inclusions, {
      feederCity: flightOrigin.name,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [charterBundleRoute, oneWayBundle, inclusions, isFeederIncluded, charterRouteInfo, flightOrigin, intl])

  const allInclusions = useMemo(() => [...bonusInclusions, ...effectiveInclusions], [bonusInclusions, effectiveInclusions])

  const scrollToPackage = useCallback(() => {
    if (!roomType) {
      viewPackageFallback()
      return
    }
    if (!scrollToVisibleOfferPageRoom(roomType.id)) {
      viewPackageFallback()
    }
  }, [roomType, viewPackageFallback])

  const onShowMoreInclusions = useCallback(() => {
    if (roomType) {
      offerPageDispatch({
        type: OfferPageActions.expandPackageInclusions,
        roomTypeId: roomType.id,
      })
    }
  }, [offerPageDispatch, roomType])

  useLayoutEffect(() => {
    if (!roomType || expandedPackageInclusions?.roomTypeId !== roomType.id) return
    if (offerPageRoomAnchorExists(roomType.id)) return
    offerPageDispatch({ type: OfferPageActions.clearExpandedPackageInclusions })
    viewPackageFallback()
  }, [expandedPackageInclusions, offerPageDispatch, roomType, viewPackageFallback])

  const viewPackage = useCallback(() => {
    trackOfferPageSuggestedPackageViewPackageClick()

    scrollToPackage()
  }, [scrollToPackage])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const isRewardSelected = rewardAvailable && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null
  const rewardPriceData = useMemo(() => {
    if (!isRewardSelected || !pricingToShow) return undefined
    const hotelPrice = pricingToShow.price - (flightPrice ?? 0)
    const hotelMemberPrice = memberPricingToShow ? memberPricingToShow.price - (flightPrice ?? 0) : undefined
    return computeRewardBundlePrice({
      hotelPrice,
      hotelMemberPrice,
      rewardPointsTotal: rewardSeatPointsTotal,
      rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
    })
  }, [isRewardSelected, pricingToShow, memberPricingToShow, flightPrice, rewardSeatPointsTotal, rewardSeatCashCoPayTotal])

  const isRewardFareSelected = isRewardFareClass(fareClass)
  const needsRewardPreview = isRewardFareSelected && !isRewardSelected

  const totalGuests = useMemo(() => Math.max(1, sumUpOccupancies(rooms)), [rooms])

  const rewardPreview = useMemo(() => {
    if (!needsRewardPreview) return undefined
    const rate = rewardPreviewRate
    if (!rate) return undefined
    const hotelPrice = pricing.price
    const hotelMemberPrice = memberPricing?.price
    const pts = rate.pointsPerPerson * totalGuests
    const cash = hotelPrice + rate.cashPerPerson * totalGuests
    const memberCash = hotelMemberPrice ? hotelMemberPrice + rate.cashPerPerson * totalGuests : undefined
    return { points: pts, cash, memberCash }
  }, [needsRewardPreview, rewardPreviewRate, pricing.price, memberPricing?.price, totalGuests])

  const nightsText = useMemo(() => {
    const nights = intl.formatMessage(
      {
        id: '1ZgK0L_Ngfs-n0KvQPTe',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Number of nights for price caption in the pricing or cost breakdown section',
      },
      { count: duration },
    )

    if (isFeederIncluded && flightOrigin) {
      if (checkInDate) {
        return intl.formatMessage(
          {
            id: '02JTtRuUp_dRFKkU18Kn',
            defaultMessage: '{nights} & {code} return flights',
            description: 'Charter hotel-and-flight caption with dates selected. nights is a complete localized night-count phrase. code is the feeder origin airport code; return flights means round-trip flights departing from it. No starting-price prefix is included.',
          },
          { nights, code: flightOrigin.code },
        )
      }
      return intl.formatMessage(
        {
          id: 'eMfZgj24YMmzqh4qEhoI',
          defaultMessage: '{nights} & {code} return flights from',
          description: 'Charter hotel-and-flight bundle price caption. {nights} is the complete localized number-and-unit phrase, such as 5 nights. {code} is the feeder departure airport code, such as SYD. Round-trip flights depart from that airport; the final from introduces a price displayed next.',
        },
        { nights, code: flightOrigin.code },
      )
    }

    return checkInDate
      ? nights
      : intl.formatMessage(
          {
            id: 'diuJQZnuRNbqVvWZP2Z7',
            defaultMessage: '{nights} from',
            description: 'Price caption before a starting price when dates are not selected. {nights} is an already-localized duration phrase including the number and night unit, such as 5 nights or 1 night; do not add another night unit. From means starting at this price, not a sender or origin.',
          },
          { nights },
        )
  }, [intl, duration, checkInDate, isFeederIncluded, flightOrigin])

  const viewPackageLabel = intl.formatMessage({
    id: '7z4-Ngi2aAhJS1Y3B-TV',
    defaultMessage: 'View package',
    description: 'Button text to view package details in suggested option preview',
  })

  const openRoomGalleryLabel = intl.formatMessage({
    id: '-t54Ywt5ilad96-rYAXi',
    defaultMessage: 'View room photos',
    description: 'Accessible label for the suggested package preview thumbnail that opens the same room photo gallery as the package image above',
  })

  const roomImages = roomType?.images
  const onRoomGalleryImageChange = useCallback((index: number, image?: App.Image) => {
    if (!image || !offer || !roomType) return
    trackEvent(carouselImageImpressionEvent(
      image,
      index + 1,
      'offer-package-gallery',
      roomGalleryListId,
      offer,
      roomType.id,
    ))
  }, [offer, roomGalleryListId, roomType])
  const onOpenRoomGallery = useCallback(() => {
    if (!roomImages?.length) return
    openOfferPageRoomGalleryLightbox(showLightbox, roomImages, onRoomGalleryImageChange)
  }, [onRoomGalleryImageChange, roomImages, showLightbox])

  const rewardUnitLabel = priceMode === 'perPerson'
    ? intl.formatMessage({
        id: '-lwhQ0fhXpw1HZWPqtFw',
        defaultMessage: 'person',
        description: 'Per-person pricing unit suffix for reward price on the suggested option preview tile',
      })
    : intl.formatMessage({
        id: 'j--PuUYLTgWUxAxROh0Z',
        defaultMessage: 'total',
        description: 'Total pricing unit suffix in the Maldives calendar',
      })

  if (!offer || !roomType) return null

  const viewProps: SuggestedOptionPreviewViewProps = {
    message,
    roomName: roomType.name,
    roomImageId: firstImage?.id ?? NO_IMAGE_AVAILABLE_ID,
    offer,
    pkg,
    checkInDate,
    charterBundleRoute,
    charterDepartDate,
    showHotelOnly: getShouldQualifyOfferHotelCancellation(offerPageState) || hasAirportTransferPackageInclusions(pkg),
    allInclusions,
    onShowMoreInclusions: isSameRoomMode ? onShowMoreInclusions : undefined,
    nightsText,
    rewardPriceData,
    needsRewardPreview,
    rewardPreview,
    pricingToShow,
    memberPricingToShow,
    convertToDisplay,
    priceMode,
    rewardUnitLabel,
    isSameRoomMode,
    onViewPackage: viewPackage,
    viewPackageLabel,
    onOpenRoomGallery: roomImages?.length ? onOpenRoomGallery : undefined,
    openRoomGalleryLabel,
  }

  return <SuggestedOptionPreviewRebrand {...viewProps} />
}

export default SuggestedOptionPreview
